/**
 * Game Lists' door to its drag demo (FTUX - Getting Started, "Bring a game up front"). Eager and import-light: React
 * and types only. The demo itself (./useDragDemo: GSAP, the FTUX's pointer) is a chunk of its own, loaded the first
 * time a list is asked to play it (`loop`, which only the FTUX's games piece turns on, through useDragDemoOn), then
 * kept, so a play cut short by the loop going off fades out. A demo that cannot load (a stale chunk after a deploy, a
 * dropped connection) or throws shows nothing: the list stays exactly as it is.
 */
import React from 'react';

import type { DragDemoProps } from './useDragDemo';

export type { DragDemoProps, DragGrip } from './useDragDemo';
export type { Takeover } from './useOwnerTakeover';

const DragDemoPlay = React.lazy(() => import('./useDragDemo'));

type DemoBoundaryProps = { children?: React.ReactNode };
type DemoBoundaryState = { failed: boolean };

/** Keeps a failed demo from taking the list with it. React still reports the error (console.error). */
class DemoBoundary extends React.Component<DemoBoundaryProps, DemoBoundaryState> {
  state: DemoBoundaryState = { failed: false };

  static getDerivedStateFromError(): DemoBoundaryState {
    return { failed: true };
  }

  render(): React.ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

/** The drag demo on one list: nothing until it is first asked to play, then the demo, playing while `loop` holds. */
export const DragDemo = (props: DragDemoProps): React.ReactElement | null => {
  const [wanted, setWanted] = React.useState(props.loop);
  if (props.loop && !wanted) setWanted(true);
  if (!wanted) return null;
  return React.createElement(
    DemoBoundary,
    null,
    React.createElement(React.Suspense, { fallback: null }, React.createElement(DragDemoPlay, props)),
  );
};

export default DragDemo;
